import { lazy, Suspense, useEffect } from 'react'
import { HashRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUser, selectAuth } from './redux/slices/authSlice'
import { useAcademicSession } from './contexts/AcademicSessionContext'
import { hasSessionHint } from './utils/authStorage'
import { landingPathFor } from './utils/landing'
import { applySchoolTheme } from './utils/schoolTheme'
import type { AppDispatch } from './redux/store'
import ChangePasswordRequired from './pages/ChangePasswordRequired'
import AcceptInvite from './pages/AcceptInvite'
import UsersAndAccess from './pages/settings/UsersAndAccess'
import UserDetail from './pages/settings/UserDetail'
import RolesPage from './pages/settings/Roles'
import SchoolDays from './pages/settings/SchoolDays'
import SchoolEmail from './pages/settings/SchoolEmail'
import SchoolPayments from './pages/settings/SchoolPayments'
import CalendarPage from './pages/calendar/CalendarPage'
import CalendarEvents from './pages/calendar/CalendarEvents'
import RoleEditor from './pages/settings/RoleEditor'
import PortalRoute from './routes/PortalRoute'

// Parents and students only download the portal; staff never download it.
const PortalApp = lazy(() => import('./portal/PortalApp'))

// Pages
import Dashboard from './pages/Dashboard'
import CentreDetails from './pages/CentreDetails'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import TenantSignupChat from './pages/TenantSignupChat'
import TenantSignupComplete from './pages/TenantSignupComplete'
import Signup from './pages/Signup'
import CntrLanding from './pages/CntrLanding'
import TmtblLanding from './pages/TmtblLanding'
import StdntLanding from './pages/StdntLanding'
import StudentInfo from './pages/StudentInfo'
import Students from './pages/Students'
import StudentProfile from './pages/StudentProfile'
import StaafStaffMembers from './pages/StaafStaffMembers'
import StaafOverview from './pages/StaafOverview'
import StaafStaffGroup from './pages/StaafStaffGroup'
import StaafOrgStructure from './pages/StaafOrgStructure'
import StaafStaffProfile from './pages/StaafStaffProfile'
import StaafRecruitment from './pages/StaafRecruitment'
import Teachers from './pages/Teachers'
import TeacherDetail from './pages/TeacherDetail'
import Duties from './pages/Duties'
import UndertakingForm from './pages/UndertakingForm'
// Students feature removed
import Candidates from './pages/Candidates'
import CandidateDetail from './pages/CandidateDetail'
import Subjects from './pages/Subjects'
import DateSheets from './pages/DateSheets'
import RoomAllocation from './pages/RoomAllocation'
import AnswerSheets from './pages/AnswerSheets'
import CentreGuidelines from './pages/CentreGuidelines'
import CBSECirculars from './pages/CBSECirculars'
import CBSEPortals from './pages/CBSEPortals'
import Home from './pages/Home'
import Staff from './pages/Staff'
import Activities from './pages/Activities'
import BellTimings from './pages/timetable/BellTimings'
import TimetableTeachers from './pages/timetable/TimetableTeachers'
import TimetableClasses from './pages/timetable/TimetableClasses'
import TimetableSubjects from './pages/timetable/TimetableSubjects'
import ClassWise from './pages/timetable/ClassWise'
import TeacherWise from './pages/timetable/TeacherWise'
import PeriodAllocation from './pages/timetable/PeriodAllocation'
import Departments from './pages/timetable/Departments'
import Substitution from './pages/timetable/Substitution'
import Versions from './pages/timetable/Versions'
import Generate from './pages/timetable/Generate'
import Formats from './pages/timetable/Formats'
import { TimetableProvider } from './contexts/TimetableContext'
import Form66 from './pages/Form66'
import SeatingPlan from './pages/SeatingPlan'
import AnswerSheetDetails from './pages/AnswerSheetDetails'
import Attendance from './pages/Attendance'
import PwdInfo from './pages/PwdInfo'
import Umcs from './pages/Umcs'
import Stickers from './pages/Stickers'
import Performas from './pages/Performas'
import DispatchSlip from './pages/DispatchSlip'
import Remuneration from './pages/Remuneration'
import RemunerationDetails from './pages/RemunerationDetails'
import DropdownExamples from './pages/DropdownExamples'
import DialogShowcase from './pages/DialogShowcase'
import Billing from './pages/Billing'
import GetStarted from './pages/GetStarted'
import AccountSettings from './pages/AccountSettings'
import Notifications from './pages/Notifications'
import HelpSupport from './pages/HelpSupport'
import SessionSelector from './pages/SessionSelector'
import Pricing from './pages/Pricing'
import ExmclCirculars from './pages/ExmclCirculars'
import ExmclResult from './pages/ExmclResult'
import ExmclReportCard from './pages/ExmclReportCard'
import ExmclAwardList from './pages/ExmclAwardList'
import ExmclAwardListFormat from './pages/ExmclAwardListFormat'
import ExmclAdmitCards from './pages/ExmclAdmitCards'
import ExmclAdmitCardFormat from './pages/ExmclAdmitCardFormat'
import ExmclReportCardFormat from './pages/ExmclReportCardFormat'
import ExmclQuestionPapers from './pages/ExmclQuestionPapers'
import ExmclSyllabus from './pages/ExmclSyllabus'
import ExmclMarksDistribution from './pages/ExmclMarksDistribution'
import ExmclExams from './pages/ExmclExams'
import ExmclDatesheets from './pages/ExmclDatesheets'
import ExmclDatesheetBuilder from './pages/ExmclDatesheetBuilder'
import ExmclSubjects from './pages/ExmclSubjects'
import ExmclCbseRegistration from './pages/ExmclCbseRegistration'
import AttndStaffAttendance from './pages/AttndStaffAttendance'
import AttndStudentAttendance from './pages/AttndStudentAttendance'
import Attndboard from './pages/Attndboard'
import AlmniDirectory from './pages/AlmniDirectory'
import TrnstOverview from './pages/TrnstOverview'
import TrnstVehicles from './pages/TrnstVehicles'
import TrnstRoutes from './pages/TrnstRoutes'
import TrnstSelfStudents from './pages/TrnstSelfStudents'
import TrnstLive from './pages/trnst/TrnstLive'
import TrnstTrips from './pages/trnst/TrnstTrips'
import TrnstAssignments from './pages/trnst/TrnstAssignments'
import TrnstCrew from './pages/trnst/TrnstCrew'
import TrnstCrewStudent from './pages/trnst/TrnstCrewStudent'
import StdntIdCards from './pages/stdnt/StdntIdCards'
import AcdmcOverview from './pages/acdmc/AcdmcOverview'
import AcdmcDashboard from './pages/acdmc/AcdmcDashboard'
import AcdmcSchoolSyllabus from './pages/acdmc/AcdmcSchoolSyllabus'
import AcdmcPlanning from './pages/acdmc/AcdmcPlanning'
import AcdmcTeaching from './pages/acdmc/AcdmcTeaching'
import AcdmcIntelligence from './pages/acdmc/AcdmcIntelligence'
import AcdmcSectionPlaceholder from './pages/acdmc/AcdmcSectionPlaceholder'
import AcdmcLessonPlan from './pages/acdmc/AcdmcLessonPlan'
import AcdmcHomework from './pages/acdmc/AcdmcHomework'
import AcdmcAssignment from './pages/acdmc/AcdmcAssignment'
import AcdmcQuiz from './pages/acdmc/AcdmcQuiz'
import AcdmcCurriculum from './pages/acdmc/AcdmcCurriculum'
import AcdmcCurriculumDetail from './pages/acdmc/AcdmcCurriculumDetail'
import AcdmcWorkDetail from './pages/acdmc/AcdmcWorkDetail'
import ActvtClubs from './pages/actvt/ActvtClubs'
import ActvtClubDetail from './pages/actvt/ActvtClubDetail'
import ActvtHouses from './pages/actvt/ActvtHouses'
import ActvtHouseDetail from './pages/actvt/ActvtHouseDetail'
import ActvtStudentCouncil from './pages/actvt/ActvtStudentCouncil'
import ActvtStudentProfile from './pages/actvt/ActvtStudentProfile'
import ActvtBoard from './pages/actvt/ActvtBoard'
import ActvtHouseRanking from './pages/actvt/ActvtHouseRanking'
import ActvtCertificates from './pages/actvt/ActvtCertificates'
import ActvtTours from './pages/actvt/ActvtTours'
import ActvtSports from './pages/actvt/ActvtSports'
import ActvtFunctions from './pages/actvt/ActvtFunctions'
import MdclCases from './pages/mdcl/MdclCases'
import MdclSupplies from './pages/mdcl/MdclSupplies'
import AsetsDashboard from './pages/asets/AsetsDashboard'
import AsetsAssets from './pages/asets/AsetsAssets'
import AsetsAssetDetail from './pages/asets/AsetsAssetDetail'
import { AsetsCategories, AsetsVendors } from './pages/asets/AsetsSimplePages'
import AsetsLocations from './pages/asets/AsetsLocations'
import AsetsLocationDetail from './pages/asets/AsetsLocationDetail'
import {
  AsetsAllocations,
  AsetsTransfers,
  AsetsMaintenance,
  AsetsStock,
  AsetsAudits,
  AsetsProcurement,
  AsetsDisposals,
  AsetsReports,
  AsetsSettings,
} from './pages/asets/AsetsWorkflowPages'
import { AppntToday, AppntList, AppntHosts, AppntAvailability, AppntReports, AppntSettings } from './pages/appnt/AppntPages'
import { LibryOverview, LibryCatalogue, LibryInventory, LibryCirculation, LibryMembers, LibryAcquisitions, LibryStocktakes, LibryReports, LibryAudit, LibryDigitalLibrary, LibryReading, LibryBarcodeDesk } from './pages/libry/LibryPages'
import CpitlDashboard from './pages/cpitl/CpitlDashboard'
import CpitlFeeStructures from './pages/cpitl/CpitlFeeStructures'
import CpitlFeeStructureDetail from './pages/cpitl/CpitlFeeStructureDetail'
import CpitlCollection from './pages/cpitl/CpitlCollection'
import CpitlStudentLedger from './pages/cpitl/CpitlStudentLedger'
import CpitlFeeSlips from './pages/cpitl/CpitlFeeSlips'
import CpitlPayments from './pages/cpitl/CpitlPayments'
import CpitlSettings from './pages/cpitl/CpitlSettings'
import CpitlExpenses from './pages/cpitl/CpitlExpenses'
import CpitlOperating from './pages/cpitl/CpitlOperating'
import CpitlFuel from './pages/cpitl/CpitlFuel'
import CpitlElectricity from './pages/cpitl/CpitlElectricity'
import CpitlInfra from './pages/cpitl/CpitlInfra'
import CpitlInfraDetail from './pages/cpitl/CpitlInfraDetail'
import CpitlBudgets from './pages/cpitl/CpitlBudgets'
import CpitlSalary from './pages/cpitl/CpitlSalary'
import CpitlPayroll from './pages/cpitl/CpitlPayroll'
import CpitlPayrollRun from './pages/cpitl/CpitlPayrollRun'
import { OnboardingPage, ValidationReportPage } from './pages/Onboarding'
import { getPublicBrandVariant } from './utils/publicBranding'

// Components
import Layout from './components/layout/Layout'
import ProtectedRoute from './routes/ProtectedRoute'
import Loader from './components/common/Loader'

const LegacyCandidatesRedirect = () => {
  const location = useLocation()
  const nextPath = location.pathname.replace(/^\/candidates/, '/candidate-details')
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />
}

// The school app (school./stageschool.capabble.cloud) opens on sign-in; marketing lives on
// capabble.cloud. Product sites keep their landing pages, and ?brand= still previews them.
const PublicLanding = () => {
  const location = useLocation()
  const variant = getPublicBrandVariant()
  const brandPreview = new URLSearchParams(location.search).has('brand')

  if (variant === 'tmtbl') return <TmtblLanding />
  if (variant === 'stdnt') return <StdntLanding />
  if (brandPreview) return <CntrLanding />
  return <Navigate to={`/login${location.search}`} replace />
}

function App() {
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, checkingSession, user } = useSelector(selectAuth)
  const { hasSession } = useAcademicSession()

  const timetableFeatureEnabled = user?.featureToggles?.timetable_classes !== false
  const isPortalUser = user?.kind === 'parent' || user?.kind === 'student'
  // Where a signed-in person goes from public pages.
  const landing = landingPathFor(user, hasSession)

  // The school's colour as the accent once we know who is signed in; Capabble blue otherwise.
  const schoolColor = isAuthenticated ? user?.tenant?.brandColor : null
  useEffect(() => applySchoolTheme(schoolColor), [schoolColor])

  useEffect(() => {
    // The session cookie is invisible to JS; if this browser was signed in, ask
    // the server who we are (it refreshes the cookie if needed).
    if (hasSessionHint()) {
      dispatch(getCurrentUser())
    }
  }, [dispatch])

  if (checkingSession) {
    return (
      <Loader size="lg" fullScreen />
    )
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Routes>
          {/* Public Routes */}
          <Route path="/pricing" element={<Pricing />} />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <PublicLanding />
              )
            }
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="/signup"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <Signup />
              )
            }
          />
          <Route
            path="/signupold"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <TenantSignupChat />
              )
            }
          />
          <Route
            path="/signup/complete"
            element={
              isAuthenticated ? (
                <Navigate to={landing} replace />
              ) : (
                <TenantSignupComplete />
              )
            }
          />

          {/* Invite link from the school: set a password and sign in */}
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* First sign-in with a temporary password */}
          <Route
            path="/change-password"
            element={!isAuthenticated ? <Navigate to="/login" replace /> : <ChangePasswordRequired />}
          />

          {/* Parent / student portal */}
          <Route element={<PortalRoute />}>
            <Route
              path="/portal/*"
              element={
                <Suspense fallback={<Loader size="lg" fullScreen />}>
                  <PortalApp />
                </Suspense>
              }
            />
          </Route>

          {/* Session Selection (authenticated but no session yet) */}
          <Route
            path="/select-session"
            element={
              !isAuthenticated ? (
                <Navigate to="/" replace />
              ) : isPortalUser || user?.mustChangePassword ? (
                <Navigate to={landing} replace />
              ) : hasSession ? (
                <Navigate to="/home" replace />
              ) : (
                <SessionSelector />
              )
            }
          />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute />}>
            <Route
              path="/"
              element={
                timetableFeatureEnabled ? (
                  <TimetableProvider>
                    <Layout />
                  </TimetableProvider>
                ) : (
                  <Layout />
                )
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route path="onboarding/validation" element={<ValidationReportPage />} />
              <Route path="home" element={<Home />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="calendar/events" element={<CalendarEvents />} />
              <Route path="school-hub" element={<Navigate to="/home" replace />} />
              <Route path="staff" element={<Staff />} />
              <Route path="activities" element={<Activities />} />
              <Route path="time-table/teachers" element={<TimetableTeachers />} />
              <Route path="time-table/classes" element={<TimetableClasses />} />
              <Route path="time-table/subjects" element={<TimetableSubjects />} />
              <Route path="time-table/departments" element={<Departments />} />
              <Route path="time-table/bell-timings" element={<BellTimings />} />
              <Route path="time-table/generate" element={<Generate />} />
              <Route path="time-table/class-wise" element={<ClassWise />} />
              <Route path="time-table/teacher-wise" element={<TeacherWise />} />
              <Route path="time-table/substitution" element={<Substitution />} />
              <Route path="time-table/versions" element={<Versions />} />
              <Route path="time-table/formats" element={<Formats />} />
              <Route path="time-table/period-distribution" element={<PeriodAllocation />} />
              <Route path="time-table/distribution" element={<Navigate to="/time-table/period-distribution" replace />} />
              <Route path="time-table/period-allocation" element={<Navigate to="/time-table/period-distribution" replace />} />
              <Route path="time-table" element={<Navigate to="/time-table/classes" replace />} />
              <Route path="stdnt/stdntboard" element={<StudentInfo />} />
              <Route path="stdnt/student-info" element={<Navigate to="/stdnt/stdntboard" replace />} />
              <Route path="stdnt/students" element={<Students />} />
              <Route path="stdnt/students/:id" element={<StudentProfile />} />
              <Route path="stdnt/classes" element={<TimetableClasses />} />
              <Route
                path="stdnt/subjects"
                element={<TimetableSubjects showParallelSubjectPairs={false} showCommonPeriod={false} />}
              />
              <Route path="stdnt/alumni" element={<AlmniDirectory />} />
              <Route path="stdnt/id-cards" element={<StdntIdCards />} />
              <Route path="stdnt" element={<Navigate to="/stdnt/stdntboard" replace />} />
              <Route path="staaf/overview" element={<StaafOverview />} />
              <Route path="staaf/organisation-structure" element={<StaafOrgStructure />} />
              <Route path="staaf/staff/:id" element={<StaafStaffProfile />} />
              <Route path="staaf/teaching-staff" element={<StaafStaffGroup group="teaching" />} />
              <Route path="staaf/sports-coach" element={<StaafStaffGroup group="sportsCoach" />} />
              <Route path="staaf/admin-staff" element={<StaafStaffGroup group="admin" />} />
              <Route path="staaf/drivers" element={<StaafStaffGroup group="drivers" />} />
              <Route path="staaf/conductors" element={<StaafStaffGroup group="conductors" />} />
              <Route path="staaf/security" element={<StaafStaffGroup group="security" />} />
              <Route path="staaf/recruitment" element={<StaafRecruitment />} />
              <Route path="staaf/staff-members" element={<StaafStaffMembers />} />
              <Route path="staaf" element={<Navigate to="/staaf/overview" replace />} />
              <Route path="attnd/attndboard" element={<Attndboard />} />
              <Route path="attnd/staff-attendance" element={<AttndStaffAttendance />} />
              <Route path="attnd/student-attendance" element={<AttndStudentAttendance />} />
              <Route path="attnd" element={<Navigate to="/attnd/attndboard" replace />} />
              <Route path="almni" element={<Navigate to="/stdnt/alumni" replace />} />
              <Route path="trnst/vehicles" element={<TrnstVehicles />} />
              <Route path="trnst/routes" element={<TrnstRoutes />} />
              <Route path="trnst/self-students" element={<TrnstSelfStudents />} />
              <Route path="trnst/live" element={<TrnstLive />} />
              <Route path="trnst/trips" element={<TrnstTrips />} />
              <Route path="trnst/assignments" element={<TrnstAssignments />} />
              <Route path="trnst/crew" element={<TrnstCrew />} />
              <Route path="trnst/crew/student/:tripId/:studentId" element={<TrnstCrewStudent />} />
              <Route path="trnst" element={<TrnstOverview />} />
              <Route path="acdmc/dashboard" element={<AcdmcDashboard />} />
              <Route path="acdmc/overview" element={<AcdmcOverview />} />
              <Route path="acdmc/lesson-plan" element={<AcdmcLessonPlan />} />
              <Route path="acdmc/homework" element={<AcdmcHomework />} />
              <Route path="acdmc/homework/:id" element={<AcdmcWorkDetail key="homework" kind="homework" />} />
              <Route path="acdmc/assignment" element={<AcdmcAssignment />} />
              <Route path="acdmc/assignment/:id" element={<AcdmcWorkDetail key="assignment" kind="assignment" />} />
              <Route path="acdmc/quiz" element={<AcdmcQuiz />} />
              <Route path="acdmc/quiz/:id" element={<AcdmcWorkDetail key="quiz" kind="quiz" />} />
              <Route path="acdmc/curriculum" element={<AcdmcCurriculum />} />
              <Route path="acdmc/curriculum/:id" element={<AcdmcCurriculumDetail />} />
              <Route
                path="acdmc/publishers"
                element={
                  <AcdmcSectionPlaceholder
                    title="Publishers and textbooks"
                    subtitle="Publisher catalogues and verified editions"
                    phase={3}
                    body="Schools will select textbook editions here without changing publisher master records."
                    primaryHref="/acdmc/curriculum"
                    primaryLabel="Open curriculum"
                  />
                }
              />
              <Route path="acdmc/syllabus" element={<AcdmcSchoolSyllabus />} />
              <Route path="acdmc/planning" element={<AcdmcPlanning />} />
              <Route path="acdmc/teaching" element={<AcdmcTeaching />} />
              <Route
                path="acdmc/assessment-alignment"
                element={
                  <AcdmcSectionPlaceholder
                    title="Assessment alignment"
                    subtitle="Exams mapped to syllabus topics"
                    phase={7}
                    body="EXMCL remains the source for exams and marks; ACDMC will store topic alignment only."
                  />
                }
              />
              <Route path="acdmc/intelligence" element={<AcdmcIntelligence />} />
              <Route
                path="acdmc/reports"
                element={
                  <AcdmcSectionPlaceholder
                    title="Academic reports"
                    subtitle="Printable and exportable views"
                    phase={7}
                    body="Reports will draw from published plans and verified teaching records."
                  />
                }
              />
              <Route path="acdmc" element={<Navigate to="/acdmc/dashboard" replace />} />
              <Route path="actvt/houses" element={<ActvtHouses />} />
              <Route path="actvt/houses/:id" element={<ActvtHouseDetail />} />
              <Route path="actvt/student-council" element={<ActvtStudentCouncil />} />
              <Route path="actvt/students/:id" element={<ActvtStudentProfile />} />
              <Route path="actvt/clubs" element={<ActvtClubs />} />
              <Route path="actvt/clubs/:id" element={<ActvtClubDetail />} />
              <Route path="actvt/board" element={<ActvtBoard />} />
              <Route path="actvt/calendar" element={<ActvtBoard />} />
              <Route path="actvt/ranking" element={<ActvtHouseRanking />} />
              <Route path="actvt/certificates" element={<ActvtCertificates />} />
              <Route path="actvt/tours" element={<ActvtTours />} />
              <Route path="actvt/sports" element={<ActvtSports />} />
              <Route path="actvt/functions" element={<ActvtFunctions />} />
              <Route path="actvt" element={<Navigate to="/actvt/houses" replace />} />
              <Route path="mdcl/cases" element={<MdclCases />} />
              <Route path="mdcl/supplies" element={<MdclSupplies />} />
              <Route path="mdcl" element={<Navigate to="/mdcl/cases" replace />} />
              <Route path="asets/dashboard" element={<AsetsDashboard />} />
              <Route path="asets/assets" element={<AsetsAssets />} />
              <Route path="asets/assets/:id" element={<AsetsAssetDetail />} />
              <Route path="asets/categories" element={<AsetsCategories />} />
              <Route path="asets/locations" element={<AsetsLocations />} />
              <Route path="asets/locations/:id" element={<AsetsLocationDetail />} />
              <Route path="asets/allocations" element={<AsetsAllocations />} />
              <Route path="asets/transfers" element={<AsetsTransfers />} />
              <Route path="asets/maintenance" element={<AsetsMaintenance />} />
              <Route path="asets/stock" element={<AsetsStock />} />
              <Route path="asets/audits" element={<AsetsAudits />} />
              <Route path="asets/vendors" element={<AsetsVendors />} />
              <Route path="asets/procurement" element={<AsetsProcurement />} />
              <Route path="asets/disposals" element={<AsetsDisposals />} />
              <Route path="asets/reports" element={<AsetsReports />} />
              <Route path="asets/settings" element={<AsetsSettings />} />
              <Route path="asets" element={<Navigate to="/asets/dashboard" replace />} />
              <Route path="libry/overview" element={<LibryOverview />} />
              <Route path="libry/catalogue" element={<LibryCatalogue />} />
              <Route path="libry/inventory" element={<LibryInventory />} />
              <Route path="libry/circulation" element={<LibryCirculation />} />
              <Route path="libry/members" element={<LibryMembers />} />
              <Route path="libry/acquisitions" element={<LibryAcquisitions />} />
              <Route path="libry/stocktakes" element={<LibryStocktakes />} />
              <Route path="libry/reports" element={<LibryReports />} />
              <Route path="libry/audit" element={<LibryAudit />} />
              <Route path="libry/digital" element={<LibryDigitalLibrary />} />
              <Route path="libry/reading" element={<LibryReading />} />
              <Route path="libry/barcode-desk" element={<LibryBarcodeDesk />} />
              <Route path="libry" element={<Navigate to="/libry/overview" replace />} />
              <Route path="appnt/today" element={<AppntToday />} />
              <Route path="appnt/appointments" element={<AppntList />} />
              <Route path="appnt/hosts" element={<AppntHosts />} />
              <Route path="appnt/availability" element={<AppntAvailability />} />
              <Route path="appnt/reports" element={<AppntReports />} />
              <Route path="appnt/settings" element={<AppntSettings />} />
              <Route path="appnt" element={<Navigate to="/appnt/today" replace />} />
              <Route path="cpitl/dashboard" element={<CpitlDashboard />} />
              <Route path="cpitl/fee-structures" element={<CpitlFeeStructures />} />
              <Route path="cpitl/fee-structures/:id" element={<CpitlFeeStructureDetail />} />
              <Route path="cpitl/collection" element={<CpitlCollection />} />
              <Route path="cpitl/students/:id" element={<CpitlStudentLedger />} />
              <Route path="cpitl/fee-slips" element={<CpitlFeeSlips />} />
              <Route path="cpitl/payments" element={<CpitlPayments />} />
              <Route path="cpitl/settings" element={<CpitlSettings />} />
              <Route path="cpitl/expenses" element={<CpitlExpenses />} />
              <Route path="cpitl/operating" element={<CpitlOperating />} />
              <Route path="cpitl/fuel" element={<CpitlFuel />} />
              <Route path="cpitl/electricity" element={<CpitlElectricity />} />
              <Route path="cpitl/infra" element={<CpitlInfra />} />
              <Route path="cpitl/infra/:id" element={<CpitlInfraDetail />} />
              <Route path="cpitl/budgets" element={<CpitlBudgets />} />
              <Route path="cpitl/salary" element={<CpitlSalary />} />
              <Route path="cpitl/payroll" element={<CpitlPayroll />} />
              <Route path="cpitl/payroll/:month" element={<CpitlPayrollRun />} />
              <Route path="cpitl" element={<Navigate to="/cpitl/dashboard" replace />} />
              <Route path="centre-details" element={<CentreDetails />} />
              <Route
                path="exam-functionaries"
                element={<Teachers hidePagination includeAllRecords uiOnlyDelete />}
              />
              <Route path="exam-functionaries/:id" element={<TeacherDetail />} />
              <Route path="duties" element={<Duties />} />
              <Route path="undertaking" element={<UndertakingForm />} />
              {/* Legacy redirects */}
              <Route path="teachers" element={<Navigate to="/exam-functionaries" replace />} />
              <Route path="teachers/:id" element={<Navigate to="/exam-functionaries/:id" replace />} />
              {/* Students feature removed */}
              <Route path="candidate-details" element={<Candidates />} />
              <Route path="candidate-details/:id" element={<CandidateDetail />} />
              <Route path="candidates/*" element={<LegacyCandidatesRedirect />} />
              <Route path="dispatch-slip" element={<DispatchSlip />} />
              <Route path="remuneration" element={<Remuneration />} />
              <Route path="remuneration/:id" element={<RemunerationDetails />} />
              <Route path="exmcl/centre-details" element={<Navigate to="/exmcl/exams" replace />} />
              <Route
                path="exmcl/exam-functionaries"
                element={
                  <Teachers
                    hidePagination
                    includeAllRecords
                    uiOnlyDelete
                    hideStats
                    disableApiMutations
                    hideSchoolCode
                    hideSchoolName
                    inlineToolbar
                    searchPlaceholder="Search name or duty"
                  />
                }
              />
              <Route path="exmcl/exam-functionaries/:id" element={<TeacherDetail />} />
              <Route path="exmcl/duties" element={<Duties />} />
              <Route path="exmcl/candidate-details" element={<Navigate to="/stdnt/students" replace />} />
              <Route path="exmcl/candidate-details/:id" element={<Navigate to="/stdnt/students" replace />} />
              <Route path="exmcl/candidates/*" element={<Navigate to="/stdnt/students" replace />} />
              <Route path="exmcl/cbse-registration" element={<ExmclCbseRegistration />} />
              <Route path="exmcl/seatingplan" element={<SeatingPlan />} />
              <Route path="exmcl/subjects" element={<ExmclSubjects />} />
              <Route path="exmcl/datesheets" element={<ExmclDatesheets />} />
              <Route path="exmcl/datesheets/:examId" element={<ExmclDatesheetBuilder />} />
              <Route path="exmcl/examrooms" element={<RoomAllocation />} />
              <Route path="exmcl/rooms" element={<Navigate to="/exmcl/examrooms" replace />} />
              <Route path="exmcl/answersheets" element={<AnswerSheets />} />
              <Route path="exmcl/answersheets/:id" element={<AnswerSheetDetails />} />
              <Route path="exmcl/attendance" element={<Attendance />} />
              <Route path="exmcl/performas/award-list" element={<ExmclAwardListFormat />} />
              <Route path="exmcl/performas/admit-card" element={<ExmclAdmitCardFormat />} />
              <Route path="exmcl/performas/report-card" element={<ExmclReportCardFormat />} />
              <Route path="exmcl/performas" element={<Performas />} />
              <Route path="exmcl/centre-guidelines" element={<ExmclCirculars />} />
              <Route path="exmcl/admit-cards" element={<ExmclAdmitCards />} />
              <Route path="exmcl/exams" element={<ExmclExams />} />
              <Route path="exmcl/result" element={<ExmclResult />} />
              <Route path="exmcl/report-card" element={<ExmclReportCard />} />
              <Route path="exmcl/award-list" element={<ExmclAwardList />} />
              <Route path="exmcl/question-papers" element={<ExmclQuestionPapers />} />
              <Route path="exmcl/syllabus" element={<ExmclSyllabus />} />
              <Route path="exmcl/marks-distribution" element={<ExmclMarksDistribution />} />
              <Route path="exmcl" element={<Navigate to="/exmcl/exams" replace />} />
              <Route path="form66" element={<Form66 />} />
              <Route path="seatingplan" element={<SeatingPlan />} />
              <Route path="subjects" element={<Subjects />} />
              <Route path="datesheets" element={<DateSheets />} />
              <Route path="examrooms" element={<RoomAllocation />} />
              {/* Legacy redirect */}
              <Route path="rooms" element={<Navigate to="/examrooms" replace />} />
              <Route path="answersheets" element={<AnswerSheets />} />
              <Route path="answersheets/:id" element={<AnswerSheetDetails />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="pwd-info" element={<PwdInfo />} />
              <Route path="umcs" element={<Umcs />} />
              <Route path="stickers" element={<Stickers />} />
              <Route path="performas" element={<Performas />} />
              <Route path="centre-guidelines" element={<CentreGuidelines />} />
              <Route path="cbse-circulars" element={<CBSECirculars />} />
              <Route path="cbse-portals" element={<CBSEPortals />} />
              <Route path="billing" element={<Billing />} />
              <Route path="get-started" element={<GetStarted />} />
              <Route path="account-settings" element={<AccountSettings />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings/users" element={<UsersAndAccess />} />
              <Route path="settings/users/:id" element={<UserDetail />} />
              <Route path="settings/roles" element={<RolesPage />} />
              <Route path="settings/school-days" element={<SchoolDays />} />
              <Route path="settings/email" element={<SchoolEmail />} />
              <Route path="settings/payments" element={<SchoolPayments />} />
              <Route path="settings/roles/new" element={<RoleEditor />} />
              <Route path="settings/roles/:roleKey" element={<RoleEditor />} />
              <Route path="help-support" element={<HelpSupport />} />
              <Route path="dropdown-examples" element={<DropdownExamples />} />
              <Route path="dialog-showcase" element={<DialogShowcase />} />
            </Route>
          </Route>

          {/* Catch all route */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">404</h1>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">Page not found</p>
                  <Link
                    to="/"
                    className="btn btn-primary"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            }
          />
        </Routes>
      </div>
    </Router>
  )
}

export default App
